import React from 'react';
import classnames from 'classnames';
import { isNil } from 'lodash';

import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';

import { sanitizeGenericHtml } from 'utils/sanitize';

import './sidenote.scss';

import type { SVGDynamicIconCustomColor } from 'types';

type SidenoteType = 'info' | 'success' | 'error'

const ICONS: Record<SidenoteType, { color: SVGDynamicIconCustomColor, name: string }> = {
  info: {
    color: 'blue',
    name: 'info-inside-circle-custom-bg-white-fg'
  },
  success: {
    color: 'primary',
    name: 'checkmark-inside-circle-custom-bg-white-fg'
  },
  error: {
    color: 'red',
    name: 'exclamation-inside-circle-custom-bg-white-fg'
  }
};

type Props = {
  className?: string,
  title?: string,
  layout?: string,
  message?: string,
  children?: React.ReactNode,
  type: SidenoteType,
  withCenteredIcon?: boolean
};

const Sidenote = ({
  message,
  type,
  title,
  className,
  layout,
  children,
  withCenteredIcon
}: Props) => {
  const sidenoteClasses = classnames('sidenote', className);

  const blockClasses = classnames({
    'sidenote__block': true,
    'sidenote__block--align-center': withCenteredIcon,
    [`sidenote__block--type-${type}`]: type
  });

  const iconClasses = classnames({
    sidenote__icon: true,
    [`sidenote__icon--type-${type}`]: type
  });

  const messageClasses = classnames({
    'sidenote__message': true,
    [`sidenote__message--type-${type}`]: type,
    'sidenote__message--horizontal': layout === 'horizontal'
  });

  const renderContent = () => {
    if (isNil(children)) {
      return <div dangerouslySetInnerHTML={{ __html: sanitizeGenericHtml(message || '') }} />;
    }
    return (
      <div>
        {children}
      </div>
    );
  };

  return (
    <section className={sidenoteClasses}>
      <div className={blockClasses}>
        <div className={iconClasses}>
          <SVGDynamicIcon
            name={ICONS[type].name}
            width={24}
            height={24}
            customColor={ICONS[type].color || 'blue'}
          />
        </div>
        <div className={messageClasses}>
          {
            title &&
            <div className="sidenote__title">
              {title}
            </div>
          }
          {renderContent()}
        </div>
      </div>
    </section>
  );
};

export default Sidenote;
